import { IColumn, IObjectWithKey } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPLink, IPShimmeredDetailsList, IPText, IPTooltipHost } from 'baseComponents';
import { IPDetailsListLayoutMode } from 'baseComponents/DetailsList/IPDetailsList';
import { IPPersona, IPPersonaSize } from 'baseComponents/Persona/IPPersona';
import { IPSearchBox } from 'baseComponents/SearchBox/IPSearchBox';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPSelection, IPSelectionMode } from 'baseComponents/Utils/Selection';
import { onRenderDetailsHeaderNoPaddingTopGlobal } from 'globalFunctions';
import { globalSearchBoxStyles, globalStackTokensGapSmall, globalTextStylesError } from 'globalStyles';
import Task from 'models/tasks/task';
import User from 'models/user';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toLocaleDateNumeric } from 'utils/datetime';
import { getHtmlJSX, htmlToText } from 'utils/html';
import { sortOnDate, sortOnString } from 'utils/sorting';
import { overflow } from 'utils/string';

interface ISubTasksListProps {
  subTasks: Task[];
  isLoading: boolean;
  onSubTaskClick?: (task: Task) => void;
  updateSubTasks: (tasks: Task[]) => void;
  selectionMode?: IPSelectionMode;
  layoutMode?: IPDetailsListLayoutMode;
  onChangeSelection?: (tasks: Task[]) => void;
  hideColumns?: string[];
  // A search box above the list, for lists long enough to need one (the audit procedure library holds
  // the procedures of a whole organization). It matches the name, the description, the checklist and
  // the form name, and it filters what is RENDERED only: sorting still writes the complete array back
  // through updateSubTasks, which is what keeps the non-matching subtasks.
  enableSearch?: boolean;
  searchPlaceholder?: string;
}

// The client id every subtask carries (SingleTask stamps loaded instances, the detail panes stamp new
// ones); the taskId is a formality for a row that somehow has none. While the list shimmers (loading
// with no subtasks yet) Fluent hands over placeholder rows that are undefined: those get their index.
const getSubTaskKey = (task: Task | undefined, index?: number): string => {
  if (!task) {
    return `placeholder-${index ?? 0}`;
  }

  return task.id ?? String(task.taskId);
};

const SubTaskList = (props: ISubTasksListProps) => {
  const { t } = useTranslation(['translation', 'tasks']);
  const appContext = useContext(AppContext);
  const [sortLoaded, setSortLoaded] = useState<boolean>(false);
  const [search, setSearch] = useState<string>('');

  // Everything a subtask can be found by, in one lower-cased string per row. A procedure of the audit
  // library is recognised as much by what it asks (its checklist) and by the form it captures the
  // result in as by its name, so all of it is searchable. The description goes through htmlToText:
  // it is stored as html, so the raw value hides a word split by markup and matches tag names.
  const searchTexts = useMemo(() => {
    const texts = new Map<Task, string>();

    for (const task of props.subTasks) {
      const parts: (string | undefined)[] = [task.name, htmlToText(task.description)];

      for (const item of task.checkList?.items ?? []) {
        parts.push(item.description, item.comment);
      }

      if (task.taskTypeId) {
        //the cache is loaded at start-up and returns a blank item for an unknown id
        parts.push(task.formVersion?.name ?? appContext.globalDataCache.taskTypes.get(task.taskTypeId).name);
      }

      texts.set(task, parts.filter(Boolean).join(' ').toLowerCase());
    }

    return texts;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.subTasks]);

  const query = search.trim().toLowerCase();
  const visibleSubTasks = query
    ? props.subTasks.filter((task) => searchTexts.get(task)?.includes(query))
    : props.subTasks;

  // One Selection for the life of the list, keyed by the subtask's client id. A fresh instance per
  // render is not the one the DetailsList holds, so clearing it did nothing; and without a key Fluent
  // tracks rows by INDEX - remove a selected row and the row that slides into its place shows up
  // selected. The callback goes through a ref so the closure never talks to a stale parent.
  const onChangeSelectionRef = useRef(props.onChangeSelection);
  onChangeSelectionRef.current = props.onChangeSelection;
  const [selection] = useState<IPSelection<Task>>(
    () =>
      new IPSelection<Task>({
        getKey: getSubTaskKey,
        onSelectionChanged: () => {
          onChangeSelectionRef.current?.(selection.getSelection() as Task[]);
        },
      }),
  );

  //
  // Effects
  //
  useEffect(() => {
    if (!props.isLoading && props.subTasks && props.subTasks.length > 0 && !sortLoaded) {
      //apply 1 time sort on changed tasks
      initialSort();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.subTasks, props.isLoading]);

  useEffect(() => {
    // A selected row the parent removed (the Delete button above the list) must not stay selected.
    // With keyed rows the ones still there keep their own state, so this only fires for a row that
    // is gone - and then the whole selection was the rows that were just deleted.
    const selected = selection.getSelection() as Task[];
    if (selected.some((task) => !props.subTasks.some((subTask) => getSubTaskKey(subTask) === getSubTaskKey(task)))) {
      selection.setAllSelected(false);
      props.onChangeSelection?.([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.subTasks]);

  //
  // Columns
  //
  const getColumns = (): IColumn[] => {
    const allColumns: IColumn[] = [
      {
        key: 'assignee',
        name: '@',
        minWidth: 40,
        maxWidth: 40,
        isResizable: false,
        isSorted: false,
        isSortedDescending: false,
        onRender: (item?: Task, index?: number, column?: IColumn) => {
          if (!item) return null;

          let user: User | undefined = undefined;
          if (item.userId) {
            user = appContext.globalDataCache.users.get(item?.userId);
          }

          return (
            <IPTooltipHost content={user?.name}>
              <IPPersona
                imageUrl={user?.getPhotoURLObj()}
                size={IPPersonaSize.size24}
                text={user?.name || ''}
                hidePersonaDetails={true}
              ></IPPersona>
            </IPTooltipHost>
          );
        },
      },
      {
        key: 'name',
        name: t('tasks:TabAllTasks.Fields.Name'),
        minWidth: 100,
        maxWidth: 250,
        isMultiline: true,
        isResizable: true,
        isSorted: true,
        isSortedDescending: false,
        onRender: (item?: Task, index?: number, column?: IColumn) => {
          if (!item) return;

          if (props.onSubTaskClick) {
            return (
              <IPTooltipHost content={getHtmlJSX(item.description, true, true) || undefined}>
                <IPText>
                  <IPLink underline onClick={props.onSubTaskClick ? () => props.onSubTaskClick!(item) : undefined}>
                    {overflow(item.name, 100)}
                  </IPLink>
                </IPText>
              </IPTooltipHost>
            );
          } else {
            return (
              <IPTooltipHost content={getHtmlJSX(item.description, true, true) || undefined}>
                <IPText>{overflow(item.name, 100)}</IPText>
              </IPTooltipHost>
            );
          }
        },
      },
      {
        key: 'description',
        name: t('tasks:TabAllTasks.Fields.Description'),
        minWidth: 150,
        maxWidth: 400,
        isMultiline: true,
        isResizable: true,
        isSorted: false,
        isSortedDescending: false,
        onRender: (item?: Task, index?: number, column?: IColumn) => {
          if (!item) return null;

          //the stored description is html; the cell shows its text, the name column tooltips the rest
          return <IPText variant="small">{overflow(htmlToText(item.description), 200)}</IPText>;
        },
      },
      {
        key: 'status',
        name: t('tasks:TabAllTasks.Fields.Status'),
        minWidth: 80,
        maxWidth: 120,
        isResizable: true,
        isSorted: false,
        isSortedDescending: false,
        onRender: (item?: Task, index?: number, column?: IColumn) => {
          if (!item) return null;

          const status = appContext.globalDataCache.taskStates.get(item.taskStateId);

          return <IPText>{status ? status?.state : ''}</IPText>;
        },
      },
      {
        key: 'deadline',
        name: t('tasks:TabAllTasks.Fields.Deadline'),
        minWidth: 70,
        maxWidth: 100,
        isResizable: true,
        isSorted: false,
        isSortedDescending: false,
        isCollapsible: true,
        onRender: (item?: Task, index?: number, column?: IColumn) => {
          if (!item) return null;

          const deadline = item.getDeadline(appContext.globalDataCache);
          let overDeadline = false;
          if (!item.completed) {
            overDeadline = item.isOverDeadline(deadline, new Date());
          }

          return (
            <IPText variant="small" styles={overDeadline ? globalTextStylesError : undefined}>
              {toLocaleDateNumeric(deadline)}
            </IPText>
          );
        },
      },
    ];

    if (props.hideColumns && props.hideColumns.length > 0) {
      return allColumns.filter((c) => props.hideColumns!.indexOf(c.key) === -1);
    } else {
      return allColumns;
    }
  };

  const [columns, setColumns] = useState<IColumn[]>(getColumns());

  //
  // Sorting
  //
  const initialSort = () => {
    const newItems = copyAndSort(props.subTasks, 'name', false);
    props.updateSubTasks(newItems);
    setSortLoaded(true);
  };

  const onColumnClick = (ev: React.MouseEvent<HTMLElement> | undefined, column: IColumn | undefined): void => {
    const newColumns: IColumn[] = columns.slice();
    const currColumn: IColumn = newColumns.filter((currCol) => column?.key === currCol.key)[0];
    for (const newCol of newColumns) {
      if (newCol === currColumn) {
        currColumn.isSortedDescending = !currColumn.isSortedDescending;
        currColumn.isSorted = true;
      } else {
        newCol.isSorted = false;
        newCol.isSortedDescending = false;
      }
    }

    const newItems = copyAndSort(props.subTasks, currColumn.key, currColumn.isSortedDescending);

    setColumns(newColumns);
    props.updateSubTasks(newItems);
  };

  function copyAndSort(items: Task[], columnKey: string, isSortedDescending?: boolean): Task[] {
    if (columnKey === 'status') {
      return items
        .slice(0)
        .sort((a: Task, b: Task) =>
          (isSortedDescending ? a.taskStateId < b.taskStateId : a.taskStateId > b.taskStateId) ? 1 : -1,
        );
    } else if (columnKey === 'name') {
      return items.slice(0).sort((a: Task, b: Task) => {
        return (isSortedDescending ? -1 : 1) * sortOnString(a.name, b.name);
      });
    } else if (columnKey === 'description') {
      return items.slice(0).sort((a: Task, b: Task) => {
        return (isSortedDescending ? -1 : 1) * sortOnString(htmlToText(a.description), htmlToText(b.description));
      });
    } else if (columnKey === 'assignee') {
      return items.slice(0).sort((a: Task, b: Task) => {
        return (isSortedDescending ? -1 : 1) * sortOnString(a.user?.name, b.user?.name);
      });
    } else if (columnKey === 'deadline') {
      return items.slice(0).sort((a: Task, b: Task) => {
        return (
          (isSortedDescending ? -1 : 1) *
          sortOnDate(a.getDeadline(appContext.globalDataCache), b.getDeadline(appContext.globalDataCache))
        );
      });
    }

    return items;
  }

  const onSearch = (value: string) => {
    //a selection made before the filter changed may no longer be on screen
    selection.setAllSelected(false);
    if (props.onChangeSelection) props.onChangeSelection([]);
    setSearch(value);
  };

  //
  // Main render
  //
  if (props.subTasks.length === 0 && !props.isLoading) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPText>{t('task:Subtasks.NoItems')}</IPText>
      </IPStack>
    );
  }

  const list = (
    <IPShimmeredDetailsList
      compact
      items={visibleSubTasks}
      columns={columns}
      getKey={getSubTaskKey}
      setKey="set"
      isHeaderVisible={true}
      enableShimmer={props.isLoading}
      shimmerLines={4}
      selection={selection as IPSelection<IObjectWithKey>}
      selectionMode={props.selectionMode || IPSelectionMode.none}
      selectionPreservedOnEmptyClick={true}
      layoutMode={props.layoutMode ?? IPDetailsListLayoutMode.justified}
      onColumnHeaderClick={onColumnClick}
      onRenderDetailsHeader={onRenderDetailsHeaderNoPaddingTopGlobal}
    />
  );

  if (!props.enableSearch) {
    return list;
  }

  return (
    <IPStack verticalFill tokens={globalStackTokensGapSmall}>
      <IPStack.Item>
        <IPSearchBox
          styles={globalSearchBoxStyles}
          underlined
          value={search}
          placeholder={props.searchPlaceholder ?? t('translation:General.Search.Placeholder')}
          onChange={(_ev, newValue) => onSearch(newValue ?? '')}
          onClear={() => onSearch('')}
        />
      </IPStack.Item>
      <IPStack.Item grow styles={{ root: { minHeight: 0 } }}>
        {visibleSubTasks.length === 0 ? (
          <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
            <IPText>{t('translation:General.Search.NoData')}</IPText>
          </IPStack>
        ) : (
          list
        )}
      </IPStack.Item>
    </IPStack>
  );
};

export default SubTaskList;
